'use client';
import React, { useEffect, useRef } from 'react';

const reassuranceItems = [
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    title: 'Activation simple',
    description: 'Quelques minutes suffisent pour créer votre livret. Aucune compétence technique requise.',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" />
      </svg>
    ),
    title: 'Tutoriel disponible',
    description: 'Un guide pas à pas vous accompagne à chaque étape de la configuration de votre livret.',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    ),
    title: 'Chatbot intégré',
    description: 'Un assistant intelligent répond aux questions de vos voyageurs, même en votre absence.',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    title: 'Support géré directement',
    description: 'L\'équipe Full-Loc prend en charge le support. Vous n\'avez rien à gérer.',
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: 'Aucun impact sur le site',
    description: 'Le livret fonctionne en complément d\'Anse des Rochers, sans modifier votre annonce existante.',
  },
];

export default function ReassuranceSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.animate-on-scroll')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 md:py-40 relative z-10 bg-white border-t border-primary/8">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Header */}
        <div className="mb-16 animate-on-scroll anim-in" style={{ animationPlayState: 'paused' }}>
          <span className="text-[10px] uppercase tracking-ultra text-accent font-medium block mb-5">
            Engagement rassurant
          </span>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight text-foreground max-w-3xl">
            Tout est prévu{' '}
            <span className="italic text-teal-gradient">pour vous faciliter</span>{' '}
            la vie.
          </h2>
        </div>

        {/* Quote */}
        <div
          className="mb-16 border-l-2 border-accent pl-8 animate-on-scroll anim-in"
          style={{ animationPlayState: 'paused' }}
        >
          <p className="font-serif-display text-2xl md:text-3xl italic text-foreground-muted leading-relaxed max-w-2xl">
            "Nous avons conçu ce livret pour que vous n'ayez rien à faire de compliqué. 
            Activez, personnalisez légèrement, et le reste se gère seul."
          </p>
          <div className="flex items-center gap-4 mt-6">
            <div className="w-8 h-px bg-accent" />
            <span className="text-[10px] uppercase tracking-ultra text-foreground-subtle">
              Équipe Full-Loc
            </span>
          </div>
        </div>

        {/* Reassurance items — alternating layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {reassuranceItems?.map((item, i) => (
            <div
              key={item?.title}
              className={`border border-primary/10 p-8 bg-background animate-on-scroll anim-in group card-lift ${
                i === 0 ? 'md:col-span-8' :
                i === 1 ? 'md:col-span-4' :
                i === 2 ? 'md:col-span-4' :
                i === 3 ? 'md:col-span-4': 'md:col-span-4'
              }`}
              style={{ animationPlayState: 'paused', animationDelay: `${i * 0.1}s` }}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-10 h-10 bg-primary/8 flex items-center justify-center text-primary group-hover:bg-primary/15 transition-colors duration-300">
                  {item?.icon}
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <path d="M7 17L17 7M7 7h10v10" />
                </svg>
              </div>
              <h3 className="font-medium text-foreground text-base mb-3">{item?.title}</h3>
              <p className="text-sm text-foreground-muted leading-relaxed">{item?.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}